<template>
  <pu-slideout-panel
    :show-panel="searchWizardStore.panelOpen"
    :position="$isDesktop ? 'left' : 'right'"
    :trigger-button-class="['filter-button']"
    class="filter-panel"
    :class="{ desktop: $isDesktop }"
    @slideout-panel-opened="filterPanelOpened"
    @slideout-panel-closed-by-button="onFilterCancel"
  >
    <template #trigger-button-contents>
      <span>
        <filter-and-sort-nav-item>
          <translate
            v-if="ifShouldShowAppliedFiltersMessage"
            class="filter-count"
            :tt="{ count: countOfAppliedFilters }"
          >
            {count} applied filters</translate
          >
        </filter-and-sort-nav-item>
      </span>
    </template>
    <filter-and-sort-main
      :already-opened="alreadyOpened"
      :placename="placename"
      @cancel="onFilterCancel"
      @filters-submit="onFiltersSubmit"
    />
  </pu-slideout-panel>
</template>

<script setup lang="ts">
import { Pinia } from 'pinia'
import { ensureArray } from '~/utility/array'

const searchWizardStore = useSearchWizardStore(usePinia() as Pinia)
await searchWizardStore.waitForDataLoad()

const bodyDataAttr = computed(() => {
  return searchWizardStore.panelOpen ? 'true' : false
})

useHead({
  bodyAttrs: {
    'data-filter-panel': bodyDataAttr,
  },
})

const searchFilters = computed(() => {
  return searchWizardStore.wizardFilters
})

const alreadyOpened = ref(false)

const placename = computed(() => {
  const route = useRoute()
  return ensureArray(route.query.placename)[0] || undefined
})

function filterPanelOpened() {
  searchWizardStore.panelOpen = true
  if (!alreadyOpened.value) {
    alreadyOpened.value = true
  }
}

function onFilterCancel() {
  if (searchWizardStore && typeof searchWizardStore.panelOpen !== 'undefined') {
    searchWizardStore.panelOpen = false
  }
}

function onFiltersSubmit() {
  if (searchWizardStore && typeof searchWizardStore.panelOpen !== 'undefined') {
    searchWizardStore.panelOpen = false
  }
}

onBeforeUnmount(() => {
  if (onFilterCancel) {
    onFilterCancel()
  }
})

const isBoundsSearch = computed(() => {
  return !!searchFilters.value.bounds
})

const facilities = computed(() => {
  return searchFilters.value.facets || []
})

const categoriesCount = computed((): number => {
  const categoryIds = searchFilters.value.categoryIds || []
  return categoryIds.length
})

const ifCategories = computed(() => {
  return !!categoriesCount.value
})

const ifPriceRange = computed(() => {
  return !!searchFilters.value.priceRange
})

const ifShouldShowAppliedFiltersMessage = computed((): boolean => {
  return (
    (facilities.value && !!facilities.value.length) ||
    isBoundsSearch.value ||
    ifPriceRange.value ||
    ifCategories.value
  )
})

const countOfAppliedFilters = computed((): number => {
  const boundsCount = isBoundsSearch.value ? 1 : 0
  const priceCount = ifPriceRange.value ? 1 : 0
  const ifCategoriesCount = ifCategories.value ? categoriesCount.value : 0
  const facilitiesCount = facilities.value ? facilities.value.length : 0
  return facilitiesCount + boundsCount + priceCount + ifCategoriesCount
})
</script>

<style>
[data-filter-panel='true'] .app-header,
[data-filter-panel='true'] .action-buttons,
[data-filter-panel='true'] .search-results-map,
[data-filter-panel='true'] .horizontal-search-bar,
[data-filter-panel='true'] .back-to-top-container {
  z-index: 0 !important;
}

[data-filter-panel='true'] {
  overflow: hidden;
}
</style>

<style scoped>
.filter-count {
  color: var(--colour-mid-blue);
  font-size: var(--small);
  font-style: italic;
  display: block;
}

.filter-panel {
  align-items: center;
  display: flex;
  flex-grow: 1;
  justify-content: center;
  padding: 0;
}

.filter-panel :deep(.filter-button) {
  border: none;
  box-shadow: none;
  padding: 0;
  height: 100%;
  width: 100%;
  border-radius: 0;
}

.filter-panel :deep(.filter-button:active) {
  transform: none;
  background: rgba(67, 89, 125, 0.4);
}

.filter-panel :deep(.x-button) {
  display: none;
}

/* desktop & tablet */
.desktop .filter-count {
  color: white;
}

.filter-panel.desktop {
  flex-grow: 0;
}

.filter-panel.desktop :deep(.slideout-panel) {
  animation-delay: 100ms;
  transform: translateZ(0);
  backface-visibility: hidden;
}
</style>
